// src/lib/airport-codes.ts
import { supabase } from './supabase'

export interface AirportCode {
  city: string
  country: string
  code: string
  continent: string
}

export async function searchAirportCodes(query: string): Promise<AirportCode[]> {
  if (!query || query.length < 2) return []

  // Clean and normalize the search query
  const cleanQuery = query.trim().replace(/\s+/g, ' ').toLowerCase()
  // Supabase `or()` uses commas to separate clauses. If the term contains a comma
  // (e.g. "Belén de Escobar, Argentina"), it breaks the filter expression.
  // Also remove diacritics so searches work regardless of accents.
  const normalized = cleanQuery
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // strip accents
    .replace(/,/g, ' ') // commas would break the OR expression
    .trim()

  try {
    const { data, error } = await supabase
      .from('airport_codes')
      .select('*')
      .or(`city.ilike.%${normalized}%,country.ilike.%${normalized}%,code.ilike.%${normalized}%`)
      .limit(15)

    if (error) {
      console.error('Error searching airport codes:', error)
      return []
    }

    // Create a Map to ensure uniqueness by code
    const uniqueResults = new Map<string, AirportCode>()
    
    // Process and sort results
    const sortedData = (data || [])
      .filter(airport => airport && airport.code && airport.city && airport.country)
      .sort((a, b) => {
        const aLowerCity = a.city.toLowerCase()
        const bLowerCity = b.city.toLowerCase()
        const aLowerCode = a.code.toLowerCase()
        const bLowerCode = b.code.toLowerCase()
        const aLowerCountry = a.country.toLowerCase()
        const bLowerCountry = b.country.toLowerCase()

        // Exact code match gets highest priority
        if (aLowerCode === normalized && bLowerCode !== normalized) return -1
        if (bLowerCode === normalized && aLowerCode !== normalized) return 1

        // Exact city match gets second priority
        if (aLowerCity === normalized && bLowerCity !== normalized) return -1
        if (bLowerCity === normalized && aLowerCity !== normalized) return 1

        // City starts with query gets third priority
        if (aLowerCity.startsWith(normalized) && !bLowerCity.startsWith(normalized)) return -1
        if (bLowerCity.startsWith(normalized) && !aLowerCity.startsWith(normalized)) return 1

        // Code starts with query gets fourth priority
        if (aLowerCode.startsWith(normalized) && !bLowerCode.startsWith(normalized)) return -1
        if (bLowerCode.startsWith(normalized) && !aLowerCode.startsWith(normalized)) return 1

        // Country exact match gets fifth priority
        if (aLowerCountry === normalized && bLowerCountry !== normalized) return -1
        if (bLowerCountry === normalized && aLowerCountry !== normalized) return 1

        // Default to alphabetical sort by city
        return aLowerCity.localeCompare(bLowerCity)
      })

    // Add sorted results to Map to ensure uniqueness
    for (const airport of sortedData) {
      if (!uniqueResults.has(airport.code)) {
        uniqueResults.set(airport.code, airport)
      }
    }

    // Convert Map back to array and limit to 10 results
    return Array.from(uniqueResults.values()).slice(0, 10)
  } catch (error) {
    console.error('Unexpected error searching airports:', error)
    return []
  }
}

// Unused or unreferenced check for delete
export function formatAirportLabel(airport: AirportCode): string {
  return `${airport.city}, ${airport.country} (${airport.code})`
}

export async function getAirportInfo(code: string): Promise<AirportCode | null> {
  if (!code || code.length !== 3) return null

  try {
    const { data, error } = await supabase
      .from('airport_codes')
      .select('*')
      .eq('code', code.toUpperCase())
      .single()

    if (error) {
      console.error('Error fetching airport info:', error)
      return null
    }

    return data
  } catch (error) {
    console.error('Unexpected error fetching airport info:', error)
    return null
  }
}

export function formatAirportLocation(airport: AirportCode): string {
  // For US airports, show state abbreviation instead of country
  if (airport.country === 'United States') {
    // This is a simplified mapping - in production you'd want a complete state mapping
    const stateMap: { [key: string]: string } = {
      'New York': 'NY',
      'California': 'CA', 
      'Florida': 'FL',
      'Illinois': 'IL',
      'Texas': 'TX',
      'Nevada': 'NV',
      'Massachusetts': 'MA',
      'Washington': 'WA',
      'Georgia': 'GA',
      'Colorado': 'CO',
      'Arizona': 'AZ'
    }
    
    // Try to extract state from city (works for entries like "Miami, FL" or just "Miami")
    const cityParts = airport.city.split(',')
    if (cityParts.length > 1) {
      return `${cityParts[0].trim()}, ${cityParts[1].trim()}`
    }
    
    // Fallback to state mapping or just city name
    const state = stateMap[airport.city] || ''
    return state ? `${airport.city}, ${state}` : airport.city
  }
  
  return `${airport.city}, ${airport.country}`
}
